import React, { useState, useRef, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { TrendingUp, Upload, FileText, LogOut, BarChart3, AlertTriangle, CheckCircle, Download, Search, UserPlus, Clock } from 'lucide-react';
import { useAuth } from '../App';
import BrandLink from './BrandLink';
import AnalysisHistory from './AnalysisHistory';
import Toast from './Toast';
import axios from 'axios';
import { apiGet } from '../utils/apiRequest';
import { withServerWakeup } from '../utils/serverWakeup';

import { BACKEND_URL } from "../utils/apiBase";
const API = `${BACKEND_URL}/api`;

const IndividualPortal = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <BrandLink />
              <span className="text-sm text-gray-500">Business Owner Portal</span>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Welcome, {user?.full_name}</span>
              <button
                onClick={logout}
                className="flex items-center space-x-1 text-gray-600 hover:text-gray-900"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Routes>
          <Route index element={<IndividualDashboard />} />
          <Route path="analyze" element={<AnalyzeFiles />} />
          <Route path="history" element={<AnalysisHistory />} />
        </Routes>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center space-x-6 text-sm text-gray-500">
            <Link to="/terms" className="hover:text-gray-700">Terms of Use</Link>
            <Link to="/privacy" className="hover:text-gray-700">Privacy Policy</Link>
            <span>© 2026 Finance Vitals</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

const IndividualDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalAnalyses: 0,
    latestHealthScore: 0,
    hasAccountant: false,
    accountantName: ''
  });
  const [recentAnalyses, setRecentAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    // Helper to show toast
    const showToastNotification = (message, type) => {
      setToastMessage(message);
      setShowToast(true);
    };

    const dashboardApiCall = async () => {
      // Use apiGet which handles 401 and token refresh automatically
      const data = await apiGet('/api/individuals/dashboard');
      
      setStats(data.stats || {
        totalAnalyses: 0,
        latestHealthScore: 0,
        hasAccountant: false,
        accountantName: ''
      });
      setRecentAnalyses(data.recent_analyses || []);
    };

    try {
      await withServerWakeup(dashboardApiCall, showToastNotification);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setToastMessage('Could not load dashboard data. Please refresh the page.');
      setShowToast(true);
      // Set default values on error
      setStats({
        totalAnalyses: 0,
        latestHealthScore: 0,
        hasAccountant: false,
        accountantName: ''
      });
      setRecentAnalyses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseToast = () => {
    setShowToast(false);
    setToastMessage('');
  };

  const handleViewAnalysisDetails = (analysis) => {
    if (analysis.analysis_id) {
      navigate(`/deep-analysis/${analysis.analysis_id}`);
    } else if (analysis.checkup_id) {
      navigate(`/results-checkup/${analysis.checkup_id}`);
    } else {
      setToastMessage('This report is no longer available to view.');
      setShowToast(true);
    }
  };

  const handleConnectAccountantClick = () => {
    setToastMessage("Coming soon — you'll be able to securely share your Finance Vitals results with your accountant or bookkeeper.");
    setShowToast(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600">Your financial health overview and recent analysis results</p>
          </div>
          
          {/* Quick Action with Tooltip */}
          <div className="relative group">
            <Link
              to="/free-checkup"
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 inline-flex items-center space-x-2 font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
              data-testid="start-new-analysis-button"
            >
              <Upload className="w-5 h-5" />
              <span>Start New Analysis</span>
            </Link>
            <div className="absolute right-0 top-full mt-2 w-64 p-3 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
              Upload your QuickBooks files (P&L, Balance Sheet, or Transactions) for instant AI-powered financial insights
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Stats Cards with Last Analysis Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-blue-100 text-blue-600">
                <FileText className="w-6 h-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Analyses</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalAnalyses}</p>
              </div>
            </div>
            <div className="group relative">
              <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center cursor-help">
                <span className="text-xs text-blue-600 font-bold">i</span>
              </div>
              <div className="absolute right-0 top-6 w-48 p-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10">
                Number of financial health reports you've generated
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="p-2 rounded-lg bg-green-100 text-green-600">
                <TrendingUp className="w-6 h-6" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Latest Health Score</p>
                <div className="flex items-center space-x-2">
                  <p className="text-2xl font-bold text-gray-900">{stats.latestHealthScore}/100</p>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    stats.latestHealthScore >= 80 ? 'bg-green-100 text-green-800' :
                    stats.latestHealthScore >= 60 ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {stats.latestHealthScore >= 80 ? 'Excellent' :
                     stats.latestHealthScore >= 60 ? 'Good' : 'Needs Work'}
                  </span>
                </div>
                {recentAnalyses.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Last updated: {new Date(recentAnalyses[0].timestamp).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
            <div className="group relative">
              <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center cursor-help">
                <span className="text-xs text-green-600 font-bold">i</span>
              </div>
              <div className="absolute right-0 top-6 w-48 p-2 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10">
                Your most recent financial health score (20-100 scale)
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-start space-x-3 mb-4">
            <UserPlus className="w-6 h-6 text-purple-600 mt-1" />
            <div>
              <h3 className="font-semibold text-gray-900">Accountant Status</h3>
              <div className="flex items-center space-x-2 mt-1">
                <p className="text-sm font-bold text-gray-900">
                  {stats.hasAccountant ? `Linked to ${stats.accountantName}` : 'Not Linked'}
                </p>
                <div className="group relative">
                  <div className="w-4 h-4 rounded-full bg-blue-100 flex items-center justify-center cursor-help">
                    <span className="text-xs text-blue-600 font-bold">?</span>
                  </div>
                  <div className="absolute left-6 top-0 w-64 p-3 bg-gray-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity z-10 pointer-events-none">
                    <p className="font-medium mb-1">Why link with your accountant?</p>
                    <ul className="space-y-1">
                      <li>• Share analyses automatically</li>
                      <li>• Get professional insights</li>
                      <li>• Streamline tax prep</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {!stats.hasAccountant && (
            <button
              onClick={handleConnectAccountantClick}
              className="inline-flex items-center px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
              data-testid="connect-accountant-button"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Connect with Your Accountant
            </button>
          )}
        </div>
      </div>

      {/* Recent Analyses */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900">Recent Analyses</h2>
            <Link to="/individual/history" className="text-green-600 hover:text-green-700 text-sm">
              View All
            </Link>
          </div>
        </div>
        <div className="divide-y divide-gray-200">
          {recentAnalyses.length > 0 ? (
            recentAnalyses.slice(0, 5).map((analysis) => (
              <div key={analysis.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900">{analysis.filename}</h3>
                    <p className="text-xs text-gray-500">
                      {new Date(analysis.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      analysis.health_score >= 80 
                        ? 'bg-green-100 text-green-800'
                        : analysis.health_score >= 60
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {analysis.health_score}/100
                    </div>
                    <button className="text-green-600 hover:text-green-700 text-sm" onClick={() => handleViewAnalysisDetails(analysis)} data-testid="view-details-button">
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-6 text-center text-gray-500">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No analyses yet</h3>
              <p className="text-gray-600 mb-4">Upload your first financial file to get started</p>
              <Link
                to="/free-checkup"
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 inline-flex items-center space-x-2"
                data-testid="start-first-analysis-button"
              >
                <Upload className="w-4 h-4" />
                <span>Start Your First Analysis</span>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Toast Notification */}
      <Toast
        message={toastMessage}
        type="error"
        isVisible={showToast}
        onClose={handleCloseToast}
        duration={4000}
        position="bottom-right"
      />
    </div>
  );
};

const AnalyzeFiles = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [healthQuestions, setHealthQuestions] = useState({
    has_no_financial_statements: false
  });
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file || !consentChecked) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    // Add consent timestamp for registered users
    const analysisData = {
      ...healthQuestions,
      consent_timestamp: new Date().toISOString(),
      consent_given: consentChecked
    };
    formData.append('health_questions', JSON.stringify(analysisData));
    
    try {
      const response = await axios.post(`${API}/upload-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setAnalysisResults(response.data);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error analyzing file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (analysisResults) {
    const { overview, health_score, red_flags, suggestions } = analysisResults;

    return (
      <div>
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Your Financial Health Report</h1>
          
          {/* Enhanced Health Score Display */}
          <div className="mt-6 bg-white rounded-2xl shadow-lg p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                    health_score >= 80 ? 'bg-green-100' : 
                    health_score >= 60 ? 'bg-yellow-100' : 'bg-red-100'
                  }`}>
                    <span className={`text-2xl ${
                      health_score >= 80 ? 'text-green-600' : 
                      health_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {health_score >= 80 ? '🌟' : 
                       health_score >= 60 ? '⚡' : '⚠️'}
                    </span>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-800">Financial Health Score</h2>
                    <div className="flex items-center space-x-3">
                      <span className={`text-3xl font-bold ${
                        health_score >= 80 ? 'text-green-600' : 
                        health_score >= 60 ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {health_score}/100
                      </span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        health_score >= 80 ? 'bg-green-100 text-green-800' : 
                        health_score >= 60 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {health_score >= 80 ? 'Excellent' : 
                         health_score >= 60 ? 'Good' : 'Needs Attention'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="w-32">
                <div className="text-xs text-gray-500 mb-1">Health Level</div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full ${
                      health_score >= 80 ? 'bg-green-500' : 
                      health_score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${health_score}%` }}
                  ></div>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {health_score >= 80 ? 'Strong' : 
                   health_score >= 60 ? 'Stable' : 'Weak'}
                </div>
              </div>
            </div>
            
            {/* Score Breakdown */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Score Breakdown:</span> This score considers your expense ratios, 
                profit trends, cash flow consistency, and financial data organization. 
                {health_score >= 80 && " Your business shows excellent financial health! 🎉"}
                {health_score >= 60 && health_score < 80 && " Your business is on a good track with room for improvement. 📈"}
                {health_score < 60 && " Your business could benefit from some financial adjustments. 💡"}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Overview</h2>
          <p className="text-gray-700 leading-relaxed">{overview}</p>
          
          {/* Warning for missing financial statements */}
          {healthQuestions.has_no_financial_statements && (
            <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
              <div className="flex items-start">
                <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-800">Score Heavily Impacted</p>
                  <p className="text-sm text-red-700 mt-1">
                    ⚠️ Your score was heavily impacted because no formal financial statements were available. 
                    Consider working with a bookkeeper to get your books in order.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {red_flags && red_flags.length > 0 && (
            <div className="bg-red-50 rounded-lg shadow-sm p-6">
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-6 h-6 text-red-600 mr-2" />
                <h2 className="text-xl font-semibold text-red-800">Areas of Concern</h2>
              </div>
              <ul className="space-y-2">
                {red_flags.map((flag, index) => (
                  <li key={index} className="text-red-700">• {flag}</li>
                ))}
              </ul>
            </div>
          )}
          
          {suggestions && suggestions.length > 0 && (
            <div className="bg-green-50 rounded-lg shadow-sm p-6">
              <div className="flex items-center mb-4">
                <CheckCircle className="w-6 h-6 text-green-600 mr-2" />
                <h2 className="text-xl font-semibold text-green-800">Recommendations</h2>
              </div>
              <ul className="space-y-2">
                {suggestions.map((suggestion, index) => (
                  <li key={index} className="text-green-700">• {suggestion}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Upload Your Financial Data</h1>
        <p className="text-gray-600">
          Upload your QuickBooks P&L, Balance Sheet, or Transaction History for an instant AI-powered health checkup
        </p>

        {/* Legal Disclaimer for Registered Users */}
        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-amber-800 mb-2">⚠️ Disclaimer</h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                The financial analysis provided by Finance Vitals is generated by an AI system based on the documents you upload. 
                It is for informational purposes only and does not constitute accounting, tax, or financial advice.
              </p>
              <p className="text-sm text-gray-700 leading-relaxed mt-2">
                No AI system can fully replace human professional judgment. We strongly recommend consulting a qualified 
                accountant or bookkeeper before making any financial decisions.
              </p>
            </div>
          </div>
        </div>

        {/* Financial Statements Question */}
        <div className="mt-4 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Do you have formal financial statements (P&L, Balance Sheet) for your business?
            <span className="ml-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
              🚨 Critical for accurate scoring
            </span>
          </label>
          <div className="space-y-2">
            <label className="flex items-center p-2 hover:bg-yellow-100 rounded">
              <input
                type="radio"
                name="financial_statements"
                checked={!healthQuestions.has_no_financial_statements}
                onChange={() => setHealthQuestions({...healthQuestions, has_no_financial_statements: false})}
                className="text-green-600 focus:ring-green-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                Yes, I have organized financial statements ✅
              </span>
            </label>
            <label className="flex items-center p-2 hover:bg-yellow-100 rounded">
              <input
                type="radio"
                name="financial_statements"
                checked={healthQuestions.has_no_financial_statements}
                onChange={() => setHealthQuestions({...healthQuestions, has_no_financial_statements: true})}
                className="text-red-600 focus:ring-red-500"
              />
              <span className="ml-2 text-sm text-gray-700">
                No, I don't have formal financial statements ⚠️
              </span>
            </label>
          </div>
          {healthQuestions.has_no_financial_statements && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-800">
                <strong>Score Impact:</strong> This will reduce your health score by 20 points. 
                Formal financial statements are foundational to good financial health.
              </p>
            </div>
          )}
        </div>

        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <TrendingUp className="w-5 h-5 text-blue-600 mt-0.5" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-blue-800">How Your Health Score Works</h3>
              <p className="text-sm text-blue-700 mt-1">
                Our AI analyzes your financial data and gives you a score from 20-100. Higher scores mean stronger financial health. 
                We look at things like your expense ratios, profit trends, and data organization to give you personalized insights.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div 
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-200 cursor-pointer ${
              file ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-green-400'
            }`}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".csv,.xlsx,.xls"
              className="hidden"
            />
            
            {file ? (
              <div>
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <p className="text-lg font-semibold text-gray-800">{file.name}</p>
              </div>
            ) : (
              <div>
                <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-semibold text-gray-800 mb-2">
                  Click to upload your file
                </p>
                <p className="text-sm text-gray-600">
                  CSV or Excel files only • Max 10MB
                </p>
              </div>
            )}
          </div>

          {/* Consent Checkbox for Registered Users */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-1 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                I understand that the results are AI-generated and for informational purposes only. 
                They do not constitute financial advice.
              </span>
            </label>
          </div>
          
          <div className="mt-6 flex justify-end">
            <button
              onClick={handleUpload}
              disabled={!file || uploading || !consentChecked}
              className={`px-8 py-3 rounded-lg font-semibold transition-colors duration-200 ${
                file && !uploading && consentChecked
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {uploading ? 'Analyzing...' : 'Analyze File'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndividualPortal;